import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { dbInsert } from '../../../lib/supabase';

const FreeConsultationCTA = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        projectBrief: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Save to Supabase / Fallback local storage
            await dbInsert('consultations', {
                ...formData,
                status: 'new',
                source: 'homepage_cta'
            });

            // Construct Gmail Web compose link
            const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=bytepairs2026@gmail.com&su=${encodeURIComponent(`New Consultation Request from ${formData.name}`)}&body=${encodeURIComponent(`Name: ${formData.name}\nCustomer Email: ${formData.email}\n\nProject Brief:\n${formData.projectBrief}`)}`;

            setSubmitSuccess(true);
            
            // Open Gmail Web Compose in a new tab
            window.open(gmailUrl, '_blank');

            setFormData({ name: '', email: '', projectBrief: '' });
        } catch (error) {
            console.error("Consultation request error:", error);
        } finally {
            setIsSubmitting(false);
        }
    };


    if (submitSuccess) {
        return (
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-20 bg-emerald-500/10 border border-emerald-500/20 rounded-[2.5rem] p-12 text-center backdrop-blur-3xl"
            >
                <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon name="CheckCircle" size={40} className="text-emerald-500" />
                </div>
                <h3 className="text-3xl font-bold text-white mb-4">Consultation Requested!</h3>
                <p className="text-emerald-100/70 text-lg max-w-xl mx-auto">
                    We've received your inquiry. A senior engineer will reach out via email within the next 2 hours to schedule your strategy session.
                </p>
                <button
                    onClick={() => setSubmitSuccess(false)}
                    className="mt-8 text-emerald-500 font-bold hover:underline"
                >
                    Request another for a different project
                </button>
            </motion.div>
        );
    }

    return (
        <motion.div
            id="consultation-form"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mt-20 relative overflow-hidden group"
        >
            {/* Background Glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 blur-3xl opacity-30 group-hover:opacity-50 transition-opacity duration-1000"></div>

            <div className="relative bg-slate-900/50 border border-white/10 rounded-[3rem] p-8 md:p-16 backdrop-blur-xl">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    <div>
                        <div className="inline-flex items-center space-x-2 bg-primary/10 border border-primary/20 text-primary px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-6">
                            <Icon name="Zap" size={14} className="animate-pulse" />
                            <span>Zero-Cost Strategy Session</span>
                        </div>
                        <h3 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                            Ready to <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">Build the Future</span>?
                        </h3>
                        <p className="text-slate-400 text-lg mb-8 leading-relaxed">
                            Stop guessing. Start building. Schedule a 30-minute free technical consultation and let's architect your next big thing.
                        </p>

                        <div className="space-y-4">
                            {[
                                { icon: 'Check', text: 'Architecture Review' },
                                { icon: 'Check', text: 'Tech Stack Strategy' },
                                { icon: 'Check', text: 'Cost & Timeline Estimation' }
                            ].map((item, i) => (
                                <div key={i} className="flex items-center space-x-3 text-slate-300">
                                    <div className="w-6 h-6 bg-emerald-500/10 rounded-full flex items-center justify-center">
                                        <Icon name={item.icon} size={12} className="text-emerald-500" />
                                    </div>
                                    <span className="font-medium">{item.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="bg-white/5 border border-white/10 rounded-3xl p-8 space-y-6">
                        <div className="space-y-4">
                            <input
                                required
                                type="text"
                                placeholder="Your Name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="w-full bg-slate-950/50 border border-white/10 rounded-xl p-4 text-white placeholder:text-slate-500 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                            />
                            <input
                                required
                                type="email"
                                placeholder="Company Email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="w-full bg-slate-950/50 border border-white/10 rounded-xl p-4 text-white placeholder:text-slate-500 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                            />
                            <textarea
                                required
                                placeholder="What are we building?"
                                rows={3}
                                value={formData.projectBrief}
                                onChange={(e) => setFormData({ ...formData, projectBrief: e.target.value })}
                                className="w-full bg-slate-950/50 border border-white/10 rounded-xl p-4 text-white placeholder:text-slate-500 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all resize-none"
                            ></textarea>
                        </div>

                        <button
                            disabled={isSubmitting}
                            className="w-full bg-gradient-to-r from-primary to-accent text-white py-5 rounded-xl font-bold text-lg shadow-brand hover:shadow-brand-lg transform hover:-translate-y-1 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed group flex items-center justify-center space-x-2"
                        >
                            {isSubmitting ? (
                                <>
                                    <Icon name="Loader2" size={20} className="animate-spin" />
                                    <span>Allocating Engineer...</span>
                                </>
                            ) : (
                                <>
                                    <span>Schedule Free Consultation</span>
                                    <Icon name="ArrowRight" size={20} className="group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </button>

                        <p className="text-center text-slate-500 text-xs text-balance">
                            No commitment required. We respect your privacy and don't spam.
                        </p>
                    </form>
                </div>
            </div>
        </motion.div>
    );
};

export default FreeConsultationCTA;
